import React from 'react'
import { Link } from 'react-router-dom'
import {
  UserIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  CogIcon,
  ArrowRightIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

const productSections = [
  {
    name: 'Profile Safe',
    description: 'KYC, Onboarding & Patron Management',
    icon: UserIcon,
    href: '/profile-safe/dashboard',
    color: 'teal',
    features: [
      'Profile Search',
      'AML/PEP Screening',
      'Profile Configuration'
    ]
  },
  {
    name: 'Monitor Safe',
    description: 'Gaming & Transaction Monitoring',
    icon: MagnifyingGlassIcon,
    href: '/monitor-safe/dashboard',
    color: 'teal',
    features: ['Monitoring Dashboard', 'Cases Dashboard', 'Rules Builder', 'Rule Testing', 'Analytics']
  },
  {
    name: 'Compliance Safe',
    description: 'Cases, Investigations, SMR & ECDD',
    icon: ShieldCheckIcon,
    href: '/compliance-safe/dashboard',
    color: 'teal',
    features: ['Compliance Dashboard', 'Case Management', 'Investigation', 'SMR Registry', 'ECDD', 'KYC Registry']
  },
  {
    name: 'PayoutSafe',
    description: 'Payout Rules & Compliance',
    icon: CurrencyDollarIcon,
    href: '/payout-safe/builder',
    color: 'yellow',
    features: ['Payout Rules Builder', 'Payout Monitoring']
  },
  {
    name: 'Governance Safe',
    description: 'Compliance Checklist, Risk & Training',
    icon: CogIcon,
    href: '/governance-safe/dashboard',
    color: 'teal',
    features: ['Governance Dashboard', 'Compliance Checklist', 'Risk Assessments', 'Training']
  },
  {
    name: 'System & Master Data',
    description: 'Administration & Configuration',
    icon: CogIcon,
    href: '/admin/group-config',
    color: 'teal-secondary',
    features: [
      'Group Configuration',
      'Venues',
      'EGM Registry',
      'User Management',
      'Activity Logs',
      'Audit Trail'
    ]
  }
]

const colorMap = {
  teal: {
    bg: 'bg-psg-teal-100 group-hover:bg-psg-teal-200',
    text: 'text-psg-teal-600',
    dot: 'bg-psg-teal-400'
  },
  'teal-secondary': {
    bg: 'bg-psg-teal-100 group-hover:bg-psg-teal-200',
    text: 'text-psg-teal-600',
    dot: 'bg-psg-teal-300'
  },
  yellow: {
    bg: 'bg-psg-yellow-100 group-hover:bg-psg-yellow-200',
    text: 'text-psg-yellow-600',
    dot: 'bg-psg-yellow-400'
  }
}

function HomePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header (title only) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <h1 className="text-3xl font-bold text-psg-teal">Welcome to PaySafeGO</h1>
        <p className="mt-1 text-base text-gray-600 max-w-3xl">
          A comprehensive club monitoring and compliance platform with integrated patron management,
          real-time monitoring, payout control, and regulatory compliance.
        </p>
      </div>

      {/* Product Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {productSections.map((section) => {
            const colors = colorMap[section.color]
            return (
              <Link
                key={section.name}
                to={section.href}
                className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-6 h-full"
              >
                <div className="flex items-center mb-4">
                  <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center transition-colors`}>
                    <section.icon className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <div className="ml-4 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-psg-teal transition-colors">
                      {section.name}
                    </h3>
                    <p className="text-sm text-gray-500">{section.description}</p>
                  </div>
                  <ArrowRightIcon className="w-5 h-5 text-gray-400 group-hover:text-psg-teal transition-colors" />
                </div>
                
                <div className="space-y-2">
                  {section.features.map((feature, index) => (
                    <div key={index} className="flex items-center text-sm text-gray-600">
                      <div className={`w-2 h-2 ${colors.dot} rounded-full mr-2`}></div>
                      {feature}
                    </div>
                  ))}
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-white border-t">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-psg-teal">24/7</div>
              <div className="text-sm text-gray-600">Real-time Monitoring</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-psg-teal">100%</div>
              <div className="text-sm text-gray-600">Compliance Coverage</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-psg-yellow">6</div>
              <div className="text-sm text-gray-600">Integrated Modules</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-psg-teal">∞</div>
              <div className="text-sm text-gray-600">Scalable Architecture</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default HomePage